import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Trash2, Plus, LogOut, MapPin, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "relumia_admin_token";

const emptyClinic = {
  name: "", doctor: "", specialty: "Dermatologist", city: "", state: "",
  area: "", pincode: "", address: "", phone: "",
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [clinics, setClinics] = useState([]);
  const [form, setForm] = useState(emptyClinic);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const authCfg = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setClinics([]);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/clinics`, authCfg());
      setClinics(data);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Session expired. Please log in again."); logout(); }
      else toast.error("Couldn't load clinics.");
    } finally {
      setLoading(false);
    }
  }, [token, authCfg, logout]);

  useEffect(() => { load(); }, [load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    }
  };

  const addClinic = async (e) => {
    e.preventDefault();
    if (!form.name || !form.city || !form.address) {
      toast.error("Name, city and address are required.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/clinics`, form, authCfg());
      toast.success("Clinic added.");
      setForm(emptyClinic);
      load();
    } catch (e) {
      if (e?.response?.status === 401) { logout(); toast.error("Session expired."); }
      else toast.error("Couldn't add clinic.");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/admin/clinics/${id}`, authCfg());
      setClinics((c) => c.filter((x) => x.id !== id));
      toast.success("Clinic removed.");
    } catch {
      toast.error("Couldn't remove clinic.");
    }
  };

  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const arr = Array.isArray(parsed) ? parsed : parsed.clinics;
      if (!Array.isArray(arr) || arr.length === 0) {
        toast.error("File must be a JSON array of clinics.");
        return;
      }
      const { data } = await axios.post(`${API}/admin/clinics/import`, { clinics: arr }, authCfg());
      toast.success(`Imported ${data.inserted}, skipped ${data.skipped}.`);
      load();
    } catch (err) {
      if (err?.response?.status === 401) { logout(); toast.error("Session expired."); }
      else toast.error(err?.response?.data?.detail?.[0]?.msg || err?.message || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full bg-transparent border-b border-[color:var(--relumia-ink)]/20 py-2.5 text-[14px] text-[color:var(--relumia-ink)] placeholder:text-[color:var(--relumia-muted)]/70 focus:outline-none focus:border-[color:var(--relumia-gold)]";

  if (!token) {
    return (
      <main className="pt-[68px] min-h-screen flex items-center justify-center px-5" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-base border border-champagne rounded-sm p-8" data-testid="admin-login-form">
          <div className="flex items-center gap-2 mb-6"><Lock size={18} className="text-gold" /><span className="overline">Admin · Clinic Manager</span></div>
          <h1 className="font-serif text-3xl text-[color:var(--relumia-ink)] mb-6">Sign in</h1>
          <input data-testid="admin-email" type="email" placeholder="Email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} className={inputCls} />
          <input data-testid="admin-password" type="password" placeholder="Password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className={`${inputCls} mt-4`} />
          <button type="submit" data-testid="admin-login-btn" className="mt-8 w-full rounded-full bg-[color:var(--relumia-ink)] px-6 py-3 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors">Sign in</button>
        </form>
      </main>
    );
  }

  return (
    <main className="pt-[68px] min-h-screen" data-testid="admin-page">
      <section className="bg-[color:var(--relumia-ink)] text-white py-12">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 flex items-center justify-between">
          <div>
            <span className="overline !text-gold">Clinic Manager</span>
            <h1 className="font-serif font-light text-3xl md:text-4xl mt-2">Dermatologist Directory</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-[13px] text-white hover:border-gold transition-colors"><LogOut size={15} /> Logout</button>
        </div>
      </section>

      <section className="pt-8">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <div className="bg-base border border-champagne rounded-sm p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6" data-testid="admin-import-card">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1"><Upload size={16} className="text-gold" /><span className="overline">Bulk import</span></div>
              <p className="text-[13px] text-[color:var(--relumia-muted)] leading-relaxed">
                Upload a JSON file (array of clinic objects with <code className="text-gold">name, doctor, specialty, city, state, area, pincode, address, phone</code>). Duplicates are skipped automatically.
              </p>
            </div>
            <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={onImportFile} data-testid="admin-import-file" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              data-testid="admin-import-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--relumia-ink)] px-6 py-3 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors disabled:opacity-60"
            >
              <Upload size={15} /> {importing ? "Importing…" : "Import JSON"}
            </button>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 grid lg:grid-cols-[0.42fr_0.58fr] gap-10">
          {/* Add form */}
          <div className="bg-base border border-champagne rounded-sm p-7 h-fit lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-5"><Plus size={16} className="text-gold" /><span className="overline">Add a clinic</span></div>
            <form onSubmit={addClinic} data-testid="admin-add-form" className="space-y-4">
              <input data-testid="clinic-field-name" placeholder="Clinic name*" value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="clinic-field-doctor" placeholder="Doctor" value={form.doctor} onChange={set("doctor")} className={inputCls} />
              <input placeholder="Specialty" value={form.specialty} onChange={set("specialty")} className={inputCls} />
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="clinic-field-city" placeholder="City*" value={form.city} onChange={set("city")} className={inputCls} />
                <input placeholder="State" value={form.state} onChange={set("state")} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input placeholder="Area" value={form.area} onChange={set("area")} className={inputCls} />
                <input data-testid="clinic-field-pincode" placeholder="Pincode" value={form.pincode} onChange={set("pincode")} className={inputCls} />
              </div>
              <input data-testid="clinic-field-address" placeholder="Full address*" value={form.address} onChange={set("address")} className={inputCls} />
              <input data-testid="clinic-field-phone" placeholder="Phone (optional, e.g. +9122...)" value={form.phone} onChange={set("phone")} className={inputCls} />
              <button type="submit" disabled={saving} data-testid="clinic-add-btn" className="w-full rounded-full bg-[color:var(--relumia-ink)] px-6 py-3 text-[13px] tracking-wide text-white hover:bg-[color:var(--relumia-gold-deep)] transition-colors disabled:opacity-60">{saving ? "Saving…" : "Add clinic"}</button>
            </form>
          </div>

          {/* List */}
          <div>
            <div className="overline mb-4">{loading ? "Loading…" : `${clinics.length} clinics`}</div>
            <div className="space-y-3" data-testid="admin-clinic-list">
              {clinics.map((c) => (
                <div key={c.id} data-testid={`admin-clinic-${c.id}`} className="bg-base border border-champagne rounded-sm p-5 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-serif text-lg text-[color:var(--relumia-ink)]">{c.name}</div>
                    <div className="text-[12.5px] text-gold">{c.doctor}</div>
                    <div className="flex items-center gap-1.5 text-[12px] text-[color:var(--relumia-muted)] mt-1"><MapPin size={13} className="text-gold" /> {c.city}{c.pincode ? ` · ${c.pincode}` : ""} · {c.phone}</div>
                  </div>
                  <button onClick={() => del(c.id)} data-testid={`clinic-delete-${c.id}`} className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[color:var(--relumia-ink)]/20 px-3 py-2 text-[12px] text-[color:var(--relumia-ink)] hover:border-red-400 hover:text-red-500 transition-colors"><Trash2 size={13} /> Remove</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
